<script setup lang="ts">
    import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
    import {
        getDataTableFrozenColumn,
        getDataTableScrollContainer,
        SCROLL_EDGE_TOLERANCE_PX,
    } from '@/js/utils/dataTableDom';

    const props = defineProps<{
        containerRef: HTMLElement | null;
    }>();

    const FADE_WIDTH_PX = 28;

    interface FadeSegment {
        top: number;
        height: number;
    }

    const left = ref(0);
    const segments = ref<FadeSegment[]>([]);
    const isScrolled = ref(false);

    let scrollContainer: HTMLElement | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let wrapperObserver: MutationObserver | null = null;
    let scrollFrame: number | null = null;

    const updatePosition = () => {
        const wrapper = props.containerRef;
        const container = getDataTableScrollContainer(props.containerRef);
        const frozenColumn = getDataTableFrozenColumn(props.containerRef);
        if (!wrapper || !container || !frozenColumn) {
            segments.value = [];
            return;
        }

        const wrapperRect = wrapper.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const frozenRect = frozenColumn.getBoundingClientRect();

        left.value = frozenRect.right - wrapperRect.left;

        const fullTop = containerRect.top - wrapperRect.top;
        const fullBottom = fullTop + containerRect.height;

        // Skip any expanded row's panel: it isn't part of the horizontally-scrolling
        // columns, so covering it would draw a stray line across unrelated content.
        const excludedRanges = Array.from(
            container.querySelectorAll<HTMLElement>('.p-datatable-row-expansion')
        )
            .map((row) => {
                const rowRect = row.getBoundingClientRect();
                return {
                    top: rowRect.top - wrapperRect.top,
                    bottom: rowRect.bottom - wrapperRect.top,
                };
            })
            .sort((a, b) => a.top - b.top);

        const result: FadeSegment[] = [];
        let cursor = fullTop;
        for (const range of excludedRanges) {
            if (range.top > cursor) {
                result.push({ top: cursor, height: range.top - cursor });
            }
            cursor = Math.max(cursor, range.bottom);
        }
        if (cursor < fullBottom) {
            result.push({ top: cursor, height: fullBottom - cursor });
        }

        segments.value = result;
    };

    const updateScrollState = () => {
        isScrolled.value = (scrollContainer?.scrollLeft ?? 0) > SCROLL_EDGE_TOLERANCE_PX;
    };

    /**
     * Vertical scroll happens inside this same container, so it also fires 'scroll';
     * segment positions must be recomputed then too, not just on horizontal scroll.
     */
    const handleScroll = () => {
        updateScrollState();

        if (scrollFrame !== null) return;
        scrollFrame = requestAnimationFrame(() => {
            scrollFrame = null;
            updatePosition();
        });
    };

    const attachScrollListener = () => {
        scrollContainer?.removeEventListener('scroll', handleScroll);
        scrollContainer = getDataTableScrollContainer(props.containerRef);
        scrollContainer?.addEventListener('scroll', handleScroll);
        handleScroll();
    };

    const attachResizeObserver = () => {
        resizeObserver?.disconnect();
        resizeObserver = null;

        if (!props.containerRef) return;

        resizeObserver = new ResizeObserver(updatePosition);
        resizeObserver.observe(props.containerRef);
    };

    const attachAll = () => {
        updatePosition();
        attachScrollListener();
        attachResizeObserver();
    };

    onMounted(() => {
        window.addEventListener('resize', updatePosition);
    });

    onBeforeUnmount(() => {
        scrollContainer?.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', updatePosition);
        resizeObserver?.disconnect();
        wrapperObserver?.disconnect();
        if (scrollFrame !== null) cancelAnimationFrame(scrollFrame);
    });

    // A template ref is null on the render that first passes it as a prop;
    // watch+immediate catches it turning real.
    watch(
        () => props.containerRef,
        (container) => {
            attachAll();

            wrapperObserver?.disconnect();
            wrapperObserver = null;
            if (container) {
                // The wrapper itself is never replaced, so no reattachment needed later.
                wrapperObserver = new MutationObserver(attachAll);
                wrapperObserver.observe(container, { childList: true, subtree: true });
            }
        },
        { immediate: true }
    );
</script>
<template>
    <div
        v-for="(segment, index) in segments"
        :key="index"
        aria-hidden="true"
        class="absolute z-30 pointer-events-none transition-opacity duration-200"
        :class="isScrolled ? 'opacity-100' : 'opacity-0'"
        :style="{
            top: `${segment.top}px`,
            left: `${left}px`,
            height: `${segment.height}px`,
            width: `${FADE_WIDTH_PX}px`,
            backgroundImage:
                'linear-gradient(to right, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.02) 45%, rgba(0,0,0,0) 100%)',
        }"
    />
</template>
